import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { i18n } from '@lingui/core';

/**
 * Вычисляет яркость (luminance) цвета в диапазоне 0-1
 * Использует формулу для относительной яркости из WCAG
 */
const getLuminance = colorValue => {
    // Маппим именованные цвета в hex
    const colorMap = {
        'white': '#ffffff',
        'yellow': '#ffff00',
        'black': '#000000',
        'red': '#ff0000',
        'green': '#008000',
        'blue': '#0000ff',
    };

    let hexColor = colorValue.toLowerCase();
    // Заменяем именованный цвет на его hex эквивалент
    if (colorMap[hexColor]) {
        hexColor = colorMap[hexColor];
    }

    // Убираем # если есть
    const hex = hexColor.replace('#', '');
    // Конвертируем hex в RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    // Применяем формулу относительной яркости WCAG
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance;
};
/**
 * Определяет, какой цвет текста использовать (белый или чёрный)
 * в зависимости от яркости фонового цвета
 */
const getContrastColor = hexColor => {
    const luminance = getLuminance(hexColor);
    // Используем более низкий порог (0.45 вместо 0.5) для лучшего контраста на светлых цветах
    // Если яркость > 0.45, значит это светлый цвет - нужен чёрный текст
    // Иначе тёмный цвет - нужен белый текст
    return luminance > 0.45 ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)';
};
/**
 * Определяет какие элементы показывать в зависимости от доступной ширины
 * Всегда показывает первый и последний, скрывает промежуточные по мере необходимости
 */
const getVisibleIndices = (itemCount, containerWidth) => {
    if (itemCount <= 2) return Array.from({ length: itemCount }, (_, i) => i);

    // Динамические пороги в зависимости от количества элементов
    // Примерно 40-45px на один видимый элемент
    let step = 1;
    let thresholdNarrow;
    let thresholdVeryNarrow;
    let thresholdExtremelyNarrow;

    // Вычисляем пороги в зависимости от количества элементов
    if (itemCount <= 5) {
        // Для небольшого количества элементов (≤5) - более щедрые пороги
        thresholdNarrow = 180;      // Узко
        thresholdVeryNarrow = 120;  // Очень узко
        thresholdExtremelyNarrow = 0;
    } else if (itemCount <= 8) {
        // Для 6-8 элементов - средние пороги
        thresholdNarrow = 260;      // Узко
        thresholdVeryNarrow = 150;  // Очень узко
        thresholdExtremelyNarrow = 0;
    } else if (itemCount <= 12) {
        // Для 9-12 элементов - более жесткие пороги
        thresholdNarrow = 320;      // Узко
        thresholdVeryNarrow = 200;  // Очень узко
        thresholdExtremelyNarrow = 0;
    } else {
        // Для 13+ элементов - максимально агрессивные пороги
        thresholdNarrow = 420;      // Узко (показываем каждый 2-й)
        thresholdVeryNarrow = 300;  // Очень узко (показываем каждый 3-й)
        thresholdExtremelyNarrow = 200;  // Экстремально узко (показываем каждый 4-й)
    }

    // Выбираем шаг в зависимости от ширины
    if (containerWidth < thresholdExtremelyNarrow) {
        // Экстремально узко - показываем каждый 4-й элемент
        step = 4;
    } else if (containerWidth < thresholdVeryNarrow) {
        // Очень узко - показываем каждый 3-й элемент
        step = 3;
    } else if (containerWidth < thresholdNarrow) {
        // Узко - показываем каждый 2-й элемент
        step = 2;
    }
    // Иначе step = 1, показываем все элементы

    // Всегда показываем первый элемент
    const visibleIndices = [ 0 ];

    // Добавляем элементы с шагом
    for (let i = step; i < itemCount - 1; i += step) {
        visibleIndices.push(i);
    }

    // Добавляем последний элемент
    if (visibleIndices[visibleIndices.length - 1] !== itemCount - 1) {
        visibleIndices.push(itemCount - 1);
    }

    return visibleIndices;
};

/**
 * GradientLegend - Универсальная легенда для визуализации градиента
 *
 * Состоит из 4 компонентов:
 * 1. Заголовок (опционально выводится LegendRenderer)
 * 2. Полоска градиента фиксированной высоты
 * 3. Текстовые надписи равномерно распределены поверх/под полоской
 * 4. Пояснение (опционально из renderConfig.description)
 *
 * Используется для: CurDeficit/Proficit, Rebalance Parking, Temperature и т.д.
 */
export const GradientLegend = ({ items, renderConfig }) => {
    const {
        gradientHeight = '20px',
        description = null,
    } = renderConfig;
    const [ isMobile, setIsMobile ] = useState(window.innerWidth < 850);
    const [ containerWidth, setContainerWidth ] = useState(0);
    const containerRef = useRef(null);
    const gradientRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 850);
            if (gradientRef.current) {
                setContainerWidth(gradientRef.current.offsetWidth);
            }
        };
        window.addEventListener('resize', handleResize);
        // Устанавливаем начальную ширину
        setTimeout(() => {
            if (gradientRef.current) {
                setContainerWidth(gradientRef.current.offsetWidth);
            }
        }, 0);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const colors = items.map(item => item.color || item.fill);
    const gradientString = `linear-gradient(90deg, ${colors.join(', ')})`;
    const height = isMobile ? '20px' : gradientHeight;
    const visibleIndices = containerWidth > 0 ? getVisibleIndices(items.length, containerWidth) : Array.from({ length: items.length }, (_, i) => i);

    return (
        <div
            style={{ marginLeft: '10px', marginRight: '10px', marginBottom: '4px' }}
            ref={containerRef}
        >
            {/* Градиент с фиксированной высотой и текстом поверх */}
            <div
                style={{
                    width: '100%',
                    height,
                    background: gradientString,
                    opacity: 0.85,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingLeft: '2px',
                    paddingRight: '2px',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    boxSizing: 'border-box',
                    position: 'relative',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    marginBottom: description ? '0px' : '0px',
                }}
                ref={gradientRef}
            >
                {items.map((item, idx) => {
                    const textColor = getContrastColor(item.color || item.fill);
                    const isVisible = visibleIndices.includes(idx);
                    const isFirst = idx === 0;
                    const isLast = idx === items.length - 1;
                    const paddingLeftValue = isFirst ? '6px' : '1px';
                    const paddingRightValue = isLast ? '6px' : '1px';

                    return (
                        <span
                            key={item.id || idx}
                            style={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                color: textColor,
                                fontWeight: '500',
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'center',
                                pointerEvents: 'none',
                                display: isVisible ? 'flex' : 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                paddingLeft: paddingLeftValue,
                                paddingRight: paddingRightValue,
                                paddingTop: '0px',
                                paddingBottom: '0px',
                                margin: '0px',
                                lineHeight: '1.2',
                            }}
                        >
                            {typeof item.label === 'string' ? item.label : i18n._(item.label)}
                        </span>
                    );
                })}
            </div>

            {/* Опциональное пояснение под легендой - в одном контейнере с градиентом */}
            {description && (
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', marginLeft: '10px', paddingTop: '8px', paddingBottom: '8px', paddingLeft: '0px', paddingRight: '2px' }}>
                    {typeof description === 'string' ? description : i18n._(description)}
                </div>
            )}
        </div>
    );
};

GradientLegend.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.oneOfType([ PropTypes.string, PropTypes.object ]).isRequired,
        color: PropTypes.string,
        fill: PropTypes.string,
    })).isRequired,
    renderConfig: PropTypes.shape({
        gradientHeight: PropTypes.string,
        description: PropTypes.oneOfType([ PropTypes.string, PropTypes.object ]),
    }).isRequired,
};
